/**
 * URL State Management Utilities
 *
 * Handles encoding/decoding component state to/from URL search parameters
 * for shareable links and browser navigation support.
 */

import { SearchMode } from 'interfaces/SearchTypes';

export interface UrlState {
  searchQuery: string;
  selectedState: string | null;
  mode: SearchMode;
}

/**
 * Encodes component state into URL search parameters
 */
export const encodeStateToUrl = (state: UrlState): URLSearchParams => {
  const params = new URLSearchParams();

  // Only add parameters that have meaningful values
  if (state.searchQuery.trim()) {
    params.set('q', state.searchQuery.trim());
  }

  if (state.selectedState) {
    params.set('state', state.selectedState);
  }

  // Only add mode if it's not the default 'search' mode
  if (state.mode === 'results') {
    params.set('view', 'results');
  }

  return params;
};

/**
 * Decodes URL search parameters into component state
 */
export const decodeUrlToState = (
  searchParams: URLSearchParams,
): Partial<UrlState> => {
  const state: Partial<UrlState> = {};

  // Decode search query
  const query = searchParams.get('q');
  if (query) {
    state.searchQuery = query;
  }

  // Decode selected state
  const selectedState = searchParams.get('state');
  if (selectedState) {
    state.selectedState = selectedState;
  }

  // Decode view mode - default to 'search' if not specified
  const view = searchParams.get('view');
  if (view === 'results') {
    state.mode = 'results';
  } else if (state.searchQuery || state.selectedState) {
    // If we have query or selected state, we should be in results mode
    state.mode = 'results';
  } else {
    state.mode = 'search';
  }

  return state;
};

/**
 * Gets the current URL search parameters
 */
export const getCurrentUrlParams = (): URLSearchParams => {
  return new URLSearchParams(window.location.search);
};

/**
 * Updates the browser URL without causing a page reload
 */
export const updateBrowserUrl = (
  params: URLSearchParams,
  replace: boolean = false,
): void => {
  const url = new URL(window.location.href);
  url.search = params.toString();

  if (replace) {
    window.history.replaceState(null, '', url.toString());
  } else {
    window.history.pushState(null, '', url.toString());
  }
};

/**
 * Validates if a state name exists in the metadata
 */
export const validateStateName = (
  stateName: string,
  stateMetadata: { [key: string]: any },
): string | null => {
  // Try exact match first (case-sensitive)
  if (stateMetadata[stateName]) {
    return stateName;
  }

  // Try case-insensitive match
  const lowerStateName = stateName.toLowerCase();
  const matchingKey = Object.keys(stateMetadata).find(
    (key) => key.toLowerCase() === lowerStateName,
  );

  if (matchingKey) {
    return matchingKey;
  }

  // Try matching by display name
  const matchingByDisplayName = Object.entries(stateMetadata).find(
    ([, metadata]) =>
      metadata.state && metadata.state.toLowerCase() === lowerStateName,
  );

  if (matchingByDisplayName) {
    return matchingByDisplayName[0];
  }

  return null;
};

/**
 * Creates a shareable URL for the current state
 */
export const createShareableUrl = (state: UrlState): string => {
  const params = encodeStateToUrl(state);
  const url = new URL(window.location.origin + window.location.pathname);
  url.search = params.toString();
  return url.toString();
};
